<template>
  <VeeForm
    ref="formRef"
    v-slot="slotForm"
    class="ds-form"
    :validation-schema="validationSchema"
    :autocomplete="autocomplete"
    :data-testid="dataTestid"
    @submit="onSubmit"
    @invalid-submit="onInvalidSubmit"
  >
    <slot v-bind="slotForm" />
  </VeeForm>
</template>

<script setup lang="ts">
import { computed, provide, ref } from "vue";
import { DsHtmlElement } from "../../helpers/DsHtmlElement";
import type {
  FormState,
  InvalidSubmissionContext,
  SubmissionContext,
} from "vee-validate";
import { Form as VeeForm } from "vee-validate";

defineOptions({
  name: "DsForm",
});

const emit = defineEmits(["submit", "invalidSubmit"]);

const props = defineProps({
  populating: {
    type: Boolean,
    default: false,
  },
  validationSchema: {
    type: Object,
    required: false,
  },
  dataTestid: {
    type: String,
    default: "ds-form",
  },
  autocomplete: {
    type: String,
    default: "off",
  },
  focusFirstInvalidField: {
    type: Boolean,
    default: true,
  },
});

provide(
  "populatingForm",
  computed((): boolean => props.populating)
);

const formRef = ref<InstanceType<typeof VeeForm> | null>(null);

const setFieldError = (field: string, message: string): void => {
  (formRef.value as InstanceType<typeof VeeForm>).setFieldError(field, message);
};

const resetForm = <T extends Record<string, unknown>>(
  state?: Partial<FormState<T>>
): void => {
  (formRef.value as InstanceType<typeof VeeForm>).resetForm(state);
};

const validate = (): void => {
  (formRef.value as InstanceType<typeof VeeForm>).validate();
};

function onSubmit(
  values: Record<string, unknown>,
  context: SubmissionContext
): void {
  emit("submit", values, context);
}

function onInvalidSubmit(context: InvalidSubmissionContext): void {
  if (props.focusFirstInvalidField) {
    const fields = Object.keys(context.errors);
    DsHtmlElement.focus(fields[0]);
  }
  emit("invalidSubmit", context);
}

defineExpose({
  setFieldError,
  resetForm,
  validate,
});
</script>
